/**
 * Vibes — burst overlay system for the recorder.
 *
 * A "vibe" is a short animated overlay (1.5–2.5s) the singer fires onto the
 * video by tapping a chip in the vibes strip. v1 ships a Starter Kit of 3
 * universal bursts rendered via Canvas2D (confetti, sparkle, hearts). Each
 * burst spawns a fixed set of particles at start and replays a closed-form
 * trajectory each frame — no per-frame physics integration, so a burst's
 * appearance is reproducible from {startTime, particles, vibeId}.
 *
 * Theme-paired kits (v2) will plug into the same `Vibe` interface — define
 * additional draw functions and attach a `VibeKit` to the relevant
 * `RecorderTheme`.
 */

export type Particle = {
  // Normalised position [0..1] — multiplied by canvas dimensions on draw so
  // bursts scale with the video frame size without re-spawning particles.
  startX: number;
  startY: number;
  // Normalised velocity per second (so distance = vx * t).
  vx: number;
  vy: number;
  rotStart: number;
  rotSpeed: number;
  color: string;
  size: number; // pixels
};

export type Vibe = {
  id: string;
  name: string;
  /** Short SVG path string used for the chip icon. Sized 24×24. */
  iconPath: string;
  /** Total lifetime of one burst in milliseconds. */
  durationMs: number;
  /** Whether this vibe is gated behind GOLD. Starter Kit vibes are false. */
  goldOnly?: boolean;
  /** Spawns particles for one burst. Called once per fired burst. */
  spawn: () => Particle[];
  /** Draws the burst onto the canvas. `tSec` = seconds since burst started. */
  draw: (ctx: CanvasRenderingContext2D, particles: Particle[], tSec: number) => void;
  /**
   * Optional macOS-reaction-style backdrop: a colour wash painted over the video
   * BEHIND the particles for the burst's duration (fades in/out), so the effect
   * pops. `peak` = max opacity (0–1). Omit for no backdrop.
   */
  backdrop?: { color: string; peak: number };
};

// ── Confetti ────────────────────────────────────────────────────────────────
const CONFETTI_COLORS = ['#ff5e8a', '#58c7c9', '#ffd24a', '#7ec850', '#a855f7', '#ff8c42'];

function spawnConfetti(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 60; i++) {
    particles.push({
      startX: 0.2 + Math.random() * 0.6,
      startY: 0.35 + Math.random() * 0.05,
      vx: (Math.random() - 0.5) * 0.9,
      vy: -0.35 - Math.random() * 0.45,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 8,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: 7 + Math.random() * 7,
    });
  }
  return particles;
}

function drawConfetti(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const gravity = 0.95;
  const fade = Math.max(0, 1 - Math.max(0, t - 1.6) / 0.6);

  for (const p of particles) {
    const px = p.startX + p.vx * t;
    const py = p.startY + p.vy * t + 0.5 * gravity * t * t;
    if (py > 1.15 || px < -0.1 || px > 1.1) continue;

    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(p.rotStart + p.rotSpeed * t);
    ctx.globalAlpha = fade;
    ctx.fillStyle = p.color;
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
    ctx.restore();
  }
}

// ── Sparkle ─────────────────────────────────────────────────────────────────
const SPARKLE_COLORS = ['#ffffff', '#ffeaa0', '#fff4c2', '#ffd24a'];

function spawnSparkle(): Particle[] {
  const particles: Particle[] = [];
  // Spread sparkles across the FULL frame (was the central 70%) with enough of them
  // to keep the density up over the larger area.
  for (let i = 0; i < 42; i++) {
    particles.push({
      startX: 0.03 + Math.random() * 0.94,
      startY: 0.05 + Math.random() * 0.9,
      vx: 0,
      vy: 0,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: 1.2 + Math.random() * 1.6,
      color: SPARKLE_COLORS[Math.floor(Math.random() * SPARKLE_COLORS.length)],
      size: 14 + Math.random() * 18,
    });
  }
  return particles;
}

function drawSparkle(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;

  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    // Stagger so sparkles pop in across the burst rather than all at once.
    const stagger = (i / particles.length) * 0.6;
    const localT = t - stagger;
    if (localT < 0) continue;
    if (localT > 1.2) continue;

    // Bell-curve scale: ramp up to 0.4, hold to 0.7, fade to 1.2.
    let scale: number;
    if (localT < 0.4) scale = localT / 0.4;
    else if (localT < 0.7) scale = 1;
    else scale = Math.max(0, 1 - (localT - 0.7) / 0.5);

    if (scale <= 0) continue;

    ctx.save();
    ctx.translate(p.startX * W, p.startY * H);
    ctx.rotate(p.rotStart + p.rotSpeed * localT);
    ctx.globalAlpha = scale;
    ctx.fillStyle = p.color;
    drawStar(ctx, 0, 0, p.size * scale, 4, 0.4);
    ctx.restore();
  }
}

function drawStar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  outerR: number,
  spikes: number,
  innerRatio: number,
): void {
  const innerR = outerR * innerRatio;
  const step = Math.PI / spikes;
  ctx.beginPath();
  for (let i = 0; i < spikes * 2; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const a = i * step - Math.PI / 2;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
}

// ── Hearts ──────────────────────────────────────────────────────────────────
const HEART_COLORS = ['#ff5e8a', '#ff85a1', '#e35887', '#ffb3c7'];

function spawnHearts(): Particle[] {
  const particles: Particle[] = [];
  // Enough hearts, spread across the FULL width, to fill the whole frame as they
  // rise — not a narrow central plume.
  for (let i = 0; i < 36; i++) {
    particles.push({
      startX: 0.02 + Math.random() * 0.96,
      // Enter from just below the bottom edge (staggered emission handles timing).
      startY: 0.98 + Math.random() * 0.14,
      vx: (Math.random() - 0.5) * 0.18,
      // Varied rise speeds so hearts spread out vertically instead of moving as a wall.
      vy: -0.32 - Math.random() * 0.42,
      rotStart: (Math.random() - 0.5) * 0.4,
      rotSpeed: (Math.random() - 0.5) * 0.6,
      color: HEART_COLORS[Math.floor(Math.random() * HEART_COLORS.length)],
      size: 20 + Math.random() * 16,
    });
  }
  return particles;
}

function drawHearts(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  // Release hearts over this many seconds so they stream up and fill the frame
  // continuously rather than rising all at once.
  const EMIT = 1.1;

  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const delay = (i / particles.length) * EMIT;
    const lt = t - delay;
    if (lt < 0) continue;

    // Hearts rise with slight horizontal sway.
    const py = p.startY + p.vy * lt;
    const sway = Math.sin(lt * 3 + p.rotStart * 4) * 0.02;
    const px = p.startX + p.vx * lt + sway;
    if (py < -0.15) continue;

    // Quick fade-in, then fade out as they rise + age.
    const fadeIn = Math.min(1, lt / 0.2);
    const fadeOut = Math.max(0, Math.min(1, 1 - lt / 2.0)) * Math.max(0, Math.min(1, py / 0.95));
    const fade = fadeIn * fadeOut;
    if (fade <= 0) continue;

    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(p.rotStart + p.rotSpeed * lt);
    ctx.globalAlpha = fade;
    ctx.fillStyle = p.color;
    drawHeart(ctx, 0, 0, p.size);
    ctx.restore();
  }
}

function drawHeart(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number): void {
  const s = size / 30;
  ctx.beginPath();
  ctx.moveTo(cx, cy + 8 * s);
  ctx.bezierCurveTo(cx, cy + 4 * s, cx - 14 * s, cy - 6 * s, cx - 14 * s, cy - 12 * s);
  ctx.bezierCurveTo(cx - 14 * s, cy - 18 * s, cx - 7 * s, cy - 18 * s, cx, cy - 10 * s);
  ctx.bezierCurveTo(cx + 7 * s, cy - 18 * s, cx + 14 * s, cy - 18 * s, cx + 14 * s, cy - 12 * s);
  ctx.bezierCurveTo(cx + 14 * s, cy - 6 * s, cx, cy + 4 * s, cx, cy + 8 * s);
  ctx.closePath();
  ctx.fill();
}

// ── Musical Notes ─────────────────────────────────────────────────────────────
const NOTE_COLORS = ['#ffffff', '#58c7c9', '#ff5e8a', '#ffd24a', '#a855f7'];

// Notes are hand-drawn with canvas paths (NOT ♪/♫ text glyphs) so they render
// identically on every system — no dependency on a music-symbol font being present.
function drawEighthNote(ctx: CanvasRenderingContext2D, s: number): void {
  const stemX = 0.14 * s;
  const stemW = 0.085 * s;
  const stemTop = -0.52 * s;
  const stemBot = 0.30 * s;
  // Stem.
  ctx.beginPath();
  ctx.rect(stemX - stemW / 2, stemTop, stemW, stemBot - stemTop);
  ctx.fill();
  // Flag off the top of the stem.
  ctx.beginPath();
  ctx.moveTo(stemX + stemW / 2, stemTop);
  ctx.quadraticCurveTo(stemX + 0.4 * s, stemTop + 0.14 * s, stemX + 0.27 * s, stemTop + 0.42 * s);
  ctx.quadraticCurveTo(stemX + 0.42 * s, stemTop + 0.16 * s, stemX + stemW / 2, stemTop + 0.22 * s);
  ctx.closePath();
  ctx.fill();
  // Slanted note head at the bottom-left of the stem.
  ctx.save();
  ctx.translate(stemX - 0.16 * s, stemBot);
  ctx.rotate(-0.35);
  ctx.beginPath();
  ctx.ellipse(0, 0, 0.22 * s, 0.16 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function drawBeamedNotes(ctx: CanvasRenderingContext2D, s: number): void {
  const x1 = -0.12 * s;
  const x2 = 0.30 * s;
  const stemW = 0.08 * s;
  const stemTop = -0.5 * s;
  // First note sits lower than the second (larger canvas-y = further down).
  const bot1 = 0.36 * s;
  const bot2 = 0.24 * s;
  // Two stems + a beam across their tops.
  ctx.beginPath();
  ctx.rect(x1 - stemW / 2, stemTop, stemW, bot1 - stemTop);
  ctx.rect(x2 - stemW / 2, stemTop, stemW, bot2 - stemTop);
  ctx.rect(x1 - stemW / 2, stemTop, x2 - x1 + stemW, 0.14 * s);
  ctx.fill();
  // Two heads.
  for (const [hx, hy] of [[x1 - 0.15 * s, bot1], [x2 - 0.15 * s, bot2]] as const) {
    ctx.save();
    ctx.translate(hx, hy);
    ctx.rotate(-0.35);
    ctx.beginPath();
    ctx.ellipse(0, 0, 0.2 * s, 0.15 * s, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

function spawnNotes(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 28; i++) {
    particles.push({
      startX: 0.04 + Math.random() * 0.92,
      startY: 0.98 + Math.random() * 0.12,
      vx: (Math.random() - 0.5) * 0.16,
      vy: -0.3 - Math.random() * 0.4,
      rotStart: (Math.random() - 0.5) * 0.4, // resting tilt
      rotSpeed: (Math.random() - 0.5) * 0.6,
      color: NOTE_COLORS[Math.floor(Math.random() * NOTE_COLORS.length)],
      size: 26 + Math.random() * 22,
    });
  }
  return particles;
}

function drawNotes(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const EMIT = 1.1; // release notes over ~1.1s so they stream up the frame

  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const delay = (i / particles.length) * EMIT;
    const lt = t - delay;
    if (lt < 0) continue;

    const py = p.startY + p.vy * lt;
    const sway = Math.sin(lt * 2.4 + p.rotStart * 6) * 0.03;
    const px = p.startX + p.vx * lt + sway;
    if (py < -0.15) continue;

    const fadeIn = Math.min(1, lt / 0.2);
    const fadeOut = Math.max(0, Math.min(1, 1 - lt / 2.0)) * Math.max(0, Math.min(1, py / 0.95));
    const fade = fadeIn * fadeOut;
    if (fade <= 0) continue;

    ctx.save();
    ctx.translate(px * W, py * H);
    // Gentle wobble tilt — notes should read upright-ish, not spin.
    ctx.rotate(p.rotStart + Math.sin(lt * 3) * 0.15);
    ctx.globalAlpha = fade;
    ctx.fillStyle = p.color;
    // Alternate single eighth notes and beamed pairs for variety.
    if (i % 2 === 0) drawEighthNote(ctx, p.size);
    else drawBeamedNotes(ctx, p.size);
    ctx.restore();
  }
}

// ── Fireworks ─────────────────────────────────────────────────────────────────
// Punchy, fully-saturated neon spread; no white so bursts never wash out under
// the additive blend.
const FIREWORK_COLORS = ['#ff0044', '#ff7a00', '#ffe600', '#22ff5e', '#00e5ff', '#2b6bff', '#c04bff', '#ff2ec4'];

// macOS-reaction-style fireworks: several big colourful shells burst across the
// WHOLE frame, shooting out fast then decelerating (ease-out), leaving glowing
// comet trails that twinkle as they fade, each with a bright center pop.
function spawnFireworks(): Particle[] {
  const particles: Particle[] = [];
  const SHELLS = 5;
  for (let s = 0; s < SHELLS; s++) {
    // Shells spread across most of the frame (not just the top).
    const cx = 0.15 + Math.random() * 0.7;
    const cy = 0.22 + Math.random() * 0.46;
    // Two colours per shell → each burst is multi-coloured, and 5 shells give a
    // rainbow spread across the frame.
    const c1 = FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];
    const c2 = FIREWORK_COLORS[Math.floor(Math.random() * FIREWORK_COLORS.length)];
    const explodeAt = s * 0.26 + Math.random() * 0.12; // popping in quick succession
    const count = 50 + Math.floor(Math.random() * 16);
    const speed = 0.22 + Math.random() * 0.13; // ≈ burst radius (ease-out saturates near this)
    // Center flash marker (size 0 → drawn as the bright explosion pop), tinted to the shell.
    particles.push({ startX: cx, startY: cy, vx: 0, vy: 0, rotStart: explodeAt, rotSpeed: 0, color: c1, size: 0 });
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * Math.PI * 2 + Math.random() * 0.12;
      const spd = speed * (0.55 + Math.random() * 0.55);
      const col = Math.random() < 0.65 ? c1 : c2;
      particles.push({
        startX: cx,
        startY: cy,
        vx: Math.cos(angle) * spd,
        vy: Math.sin(angle) * spd,
        rotStart: explodeAt, // repurposed: this shell's explosion time (seconds)
        rotSpeed: Math.random() * Math.PI * 2, // twinkle phase
        color: col,
        size: 2.4 + Math.random() * 2.2,
      });
    }
  }
  return particles;
}

function drawFireworks(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const GRAV = 0.18; // gentle downward droop
  const LIFE = 1.9; // seconds a spark lives after its shell explodes
  // Additive blend → overlapping sparks glow bright, like real fireworks.
  ctx.globalCompositeOperation = 'lighter';
  ctx.lineCap = 'round';

  for (const p of particles) {
    const lt = t - p.rotStart; // time since this shell exploded
    if (lt < 0) continue;

    // Center flash pop (the size-0 marker).
    if (p.size === 0) {
      if (lt > 0.24) continue;
      const k = lt / 0.24;
      ctx.globalAlpha = (1 - k) * 0.85;
      ctx.fillStyle = p.color; // tinted to the shell's colour, not white
      ctx.beginPath();
      ctx.arc(p.startX * W, p.startY * H, 3 + k * 34, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }

    if (lt > LIFE) continue;

    // Ease-out expansion: shoots out fast, then decelerates toward its radius.
    const travel = 1 - Math.exp(-lt * 3.6);
    const px = p.startX + p.vx * travel;
    const py = p.startY + p.vy * travel + 0.5 * GRAV * lt * lt;
    if (py > 1.12) continue;

    // Slightly-earlier position → comet trail (long while fast, short while slow).
    const u2 = lt > 0.05 ? lt - 0.05 : 0;
    const tr2 = 1 - Math.exp(-u2 * 3.6);
    const px2 = p.startX + p.vx * tr2;
    const py2 = p.startY + p.vy * tr2 + 0.5 * GRAV * u2 * u2;

    const life = 1 - lt / LIFE;
    const twinkle = 0.6 + 0.4 * Math.sin(lt * 32 + p.rotSpeed);
    const alpha = life * (lt > LIFE * 0.4 ? twinkle : 1); // twinkle in the back half
    if (alpha <= 0.02) continue;

    const x = px * W;
    const y = py * H;
    // Comet trail.
    ctx.globalAlpha = alpha * 0.5;
    ctx.strokeStyle = p.color;
    ctx.lineWidth = p.size * 0.85;
    ctx.beginPath();
    ctx.moveTo(px2 * W, py2 * H);
    ctx.lineTo(x, y);
    ctx.stroke();
    // Glowing head.
    ctx.globalAlpha = alpha;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(x, y, p.size, 0, Math.PI * 2);
    ctx.fill();
  }
}

// ── Bubbles ───────────────────────────────────────────────────────────────────
const BUBBLE_COLORS = ['#8fd7ff', '#bfefff', '#ffffff', '#a0e8e0'];

function spawnBubbles(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 26; i++) {
    particles.push({
      startX: 0.04 + Math.random() * 0.92,
      startY: 1.0 + Math.random() * 0.12,
      vx: (Math.random() - 0.5) * 0.1,
      vy: -0.28 - Math.random() * 0.24, // slow, gentle rise
      rotStart: Math.random() * Math.PI * 2, // wobble phase
      rotSpeed: 1.5 + Math.random() * 2, // wobble speed
      color: BUBBLE_COLORS[Math.floor(Math.random() * BUBBLE_COLORS.length)],
      size: 14 + Math.random() * 26,
    });
  }
  return particles;
}

function drawBubbles(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const EMIT = 1.2;

  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const delay = (i / particles.length) * EMIT;
    const lt = t - delay;
    if (lt < 0) continue;

    const py = p.startY + p.vy * lt;
    const wobble = Math.sin(lt * p.rotSpeed + p.rotStart) * 0.025;
    const px = p.startX + p.vx * lt + wobble;
    if (py < -0.15) continue;

    const fadeIn = Math.min(1, lt / 0.3);
    const fadeTop = Math.min(1, py / 0.12); // fade as they reach the top
    const fadeAge = Math.max(0, 1 - lt / 2.8);
    const fade = fadeIn * fadeTop * fadeAge;
    if (fade <= 0) continue;

    const r = p.size;
    const x = px * W;
    const y = py * H;

    ctx.save();
    // Translucent body.
    ctx.globalAlpha = fade * 0.32;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    // Rim.
    ctx.globalAlpha = fade * 0.8;
    ctx.lineWidth = Math.max(1, r * 0.06);
    ctx.strokeStyle = p.color;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    // Highlight glint.
    ctx.globalAlpha = fade * 0.9;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.beginPath();
    ctx.arc(x - r * 0.32, y - r * 0.32, Math.max(1, r * 0.16), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ── Lightning ─────────────────────────────────────────────────────────────────
// A full-frame effect rather than particles: the frame dims like a storm, then
// bright white flashes strobe over it (with a jagged bolt on the big flash) —
// the "bright and/or dark" movie-lightning look. Particles store the bolt vertices.
function spawnLightning(): Particle[] {
  const particles: Particle[] = [];
  const segments = 9;
  let x = 0.3 + Math.random() * 0.4; // bolt origin near the top
  for (let i = 0; i <= segments; i++) {
    x += (Math.random() - 0.5) * 0.12; // horizontal jitter as it descends
    particles.push({
      startX: Math.min(0.95, Math.max(0.05, x)),
      startY: i / segments, // 0 (top) → 1 (bottom)
      vx: 0,
      vy: 0,
      rotStart: 0,
      rotSpeed: 0,
      color: '#eaf2ff',
      size: 0,
    });
  }
  return particles;
}

// Sum of fast-decaying spikes → a storm strobe: quick double, pause, big flash, flicker.
function lightningFlash(t: number): number {
  const spikes: [number, number][] = [
    [0.0, 0.9],
    [0.11, 0.7],
    [0.5, 1.0],
    [0.62, 0.6],
    [0.72, 0.35],
  ];
  let v = 0;
  for (const [time, peak] of spikes) {
    const dt = t - time;
    if (dt >= 0) v += peak * Math.exp(-dt / 0.12);
  }
  return Math.min(1, v);
}

function drawLightning(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const DUR = 1.3;
  if (t > DUR) return;

  // Overall envelope: fade in fast, ease out at the end.
  const active = Math.min(1, t / 0.05) * Math.max(0, Math.min(1, (DUR - t) / 0.3));
  const flash = lightningFlash(t) * active;

  // Storm dim between flashes so the flashes read bright by contrast.
  const dark = active * 0.4 * (1 - flash);
  if (dark > 0.01) {
    ctx.save();
    ctx.globalAlpha = dark;
    ctx.fillStyle = '#05070f';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  // Bright white flash over the whole frame.
  if (flash > 0.01) {
    ctx.save();
    ctx.globalAlpha = Math.min(1, flash);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();
  }

  // The bolt, glowing during the big flash (~t=0.5s).
  const boltGlow = Math.exp(-Math.abs(t - 0.5) / 0.12) * active;
  if (boltGlow > 0.05 && particles.length > 1) {
    ctx.save();
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#ffffff';
    ctx.shadowColor = '#8ab6ff';
    ctx.shadowBlur = 24;
    ctx.globalAlpha = Math.min(1, boltGlow);
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(particles[0].startX * W, particles[0].startY * H);
    for (let i = 1; i < particles.length; i++) {
      ctx.lineTo(particles[i].startX * W, particles[i].startY * H);
    }
    ctx.stroke();
    ctx.restore();
  }
}

// ── Fire ──────────────────────────────────────────────────────────────────────
const EMBER_COLORS = ['#ffb347', '#ff8c1a', '#ffd27f', '#ff6a00'];

const FIRE_LIFE = 1.5; // seconds a flame particle takes to rise + burn out

// Colour of a flame particle by its age (0 = hot base → 1 = cool tip).
function fireColor(age: number): [number, number, number] {
  if (age < 0.22) {
    const f = age / 0.22; // white-yellow → yellow
    return [255, (255 - f * 70) | 0, (210 - f * 160) | 0];
  }
  if (age < 0.55) {
    const f = (age - 0.22) / 0.33; // yellow → orange-red
    return [255, (185 - f * 130) | 0, (50 - f * 40) | 0];
  }
  const f = Math.min(1, (age - 0.55) / 0.45); // red, darkening
  return [(255 - f * 100) | 0, (55 - f * 45) | 0, 10];
}

function spawnFlames(): Particle[] {
  const particles: Particle[] = [];
  // Many soft flame particles rising from across the bottom; each loops through
  // its own rise-cycle (staggered via rotStart) so the fire is continuous and,
  // stacked additively, builds a bright hot base that breaks into wispy tips.
  for (let i = 0; i < 120; i++) {
    particles.push({
      startX: 0.03 + Math.random() * 0.94,
      startY: 1.0 + Math.random() * 0.03,
      vx: 0.015 + Math.random() * 0.04, // wobble amplitude
      vy: 0.55 + Math.random() * 0.45, // rise height
      rotStart: Math.random() * FIRE_LIFE, // cycle phase offset
      rotSpeed: 2.5 + Math.random() * 3, // wobble frequency
      color: '',
      size: 18 + Math.random() * 26, // base radius (px)
    });
  }
  // Rising embers/sparks — negative size flags an ember.
  for (let i = 0; i < 22; i++) {
    particles.push({
      startX: 0.08 + Math.random() * 0.84,
      startY: 0.92 + Math.random() * 0.08,
      vx: (Math.random() - 0.5) * 0.12,
      vy: -0.35 - Math.random() * 0.45,
      rotStart: Math.random() * 1.4,
      rotSpeed: 3 + Math.random() * 4,
      color: EMBER_COLORS[Math.floor(Math.random() * EMBER_COLORS.length)],
      size: -(1.5 + Math.random() * 2.5),
    });
  }
  return particles;
}

function drawFlames(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const DUR = 2.4;
  if (t > DUR) return;
  const env = Math.min(1, t / 0.25) * Math.max(0, Math.min(1, (DUR - t) / 0.4));
  ctx.globalCompositeOperation = 'lighter'; // additive → dense base builds a hot core

  for (const p of particles) {
    if (p.size < 0) {
      // Ember: short rise-and-fade cycles → a continuous spark stream.
      const cyc = 1.3;
      const lt = (t + p.rotStart) % cyc;
      const py = p.startY + p.vy * lt;
      if (py < 0) continue;
      const px = p.startX + p.vx * lt + Math.sin(lt * p.rotSpeed) * 0.012;
      const fade = Math.max(0, 1 - lt / cyc) * (0.55 + 0.45 * Math.sin(t * p.rotSpeed * 2 + p.rotStart));
      if (fade <= 0.02) continue;
      ctx.globalAlpha = fade * env;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(px * W, py * H, Math.abs(p.size), 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    // Flame particle: rises through its cycle, cooling + shrinking, with turbulent
    // horizontal wobble that grows as it rises (so tips lick and break up).
    const lt = (t + p.rotStart) % FIRE_LIFE;
    const age = lt / FIRE_LIFE;
    const py = p.startY - age * p.vy;
    const wob =
      (Math.sin(lt * p.rotSpeed + p.rotStart * 5) + 0.5 * Math.sin(lt * p.rotSpeed * 2.3 + p.rotStart)) *
      p.vx *
      (0.25 + age * 1.3);
    const px = p.startX + wob;
    const r = p.size * (1 - age * 0.55);
    if (r < 1) continue;
    const alpha = Math.min(1, lt / 0.08) * Math.pow(1 - age, 1.3) * env;
    if (alpha < 0.02) continue;
    const [cr, cg, cb] = fireColor(age);
    const x = px * W;
    const y = py * H;
    // Stretch each element vertically (more as it rises) → tapered flame streaks,
    // not round blobs.
    const stretch = 1.7 + age * 1.4;
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(1, stretch);
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
    g.addColorStop(0, `rgba(${cr},${cg},${cb},${alpha})`);
    g.addColorStop(0.6, `rgba(${cr},${cg},${cb},${alpha * 0.5})`);
    g.addColorStop(1, `rgba(${cr},${cg},${cb},0)`);
    ctx.globalAlpha = 1;
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(0, 0, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ── Balloons ──────────────────────────────────────────────────────────────────
const BALLOON_COLORS = ['#ff4d6d', '#4dabff', '#ffd23f', '#5ee085', '#c04bff', '#ff8c42'];

function spawnBalloons(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 15; i++) {
    particles.push({
      startX: 0.06 + Math.random() * 0.88,
      startY: 1.1 + Math.random() * 0.2,
      vx: (Math.random() - 0.5) * 0.05,
      vy: -0.26 - Math.random() * 0.2,
      rotStart: Math.random() * Math.PI * 2, // sway phase
      rotSpeed: 0.8 + Math.random() * 1.0,
      color: BALLOON_COLORS[Math.floor(Math.random() * BALLOON_COLORS.length)],
      size: 46 + Math.random() * 30,
    });
  }
  return particles;
}

function drawBalloons(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const EMIT = 1.1;
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const delay = (i / particles.length) * EMIT;
    const lt = t - delay;
    if (lt < 0) continue;
    const py = p.startY + p.vy * lt;
    const sway = Math.sin(lt * p.rotSpeed + p.rotStart) * 0.03;
    const px = p.startX + p.vx * lt + sway;
    if (py < -0.3) continue;
    const fade = Math.min(1, lt / 0.3) * Math.max(0, Math.min(1, (py + 0.25) / 0.2));
    if (fade <= 0.02) continue;
    const r = p.size;
    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(Math.sin(lt * p.rotSpeed + p.rotStart) * 0.12);
    ctx.globalAlpha = fade;
    // String.
    ctx.strokeStyle = 'rgba(255,255,255,0.4)';
    ctx.lineWidth = Math.max(1, r * 0.02);
    ctx.beginPath();
    ctx.moveTo(0, r * 0.5);
    ctx.quadraticCurveTo(r * 0.12, r * 0.8, 0, r * 1.05);
    ctx.stroke();
    // Knot.
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.moveTo(-r * 0.06, r * 0.5);
    ctx.lineTo(r * 0.06, r * 0.5);
    ctx.lineTo(0, r * 0.58);
    ctx.closePath();
    ctx.fill();
    // Body.
    ctx.beginPath();
    ctx.ellipse(0, 0, r * 0.4, r * 0.48, 0, 0, Math.PI * 2);
    ctx.fill();
    // Highlight.
    ctx.globalAlpha = fade * 0.5;
    ctx.fillStyle = 'rgba(255,255,255,0.85)';
    ctx.beginPath();
    ctx.ellipse(-r * 0.14, -r * 0.16, r * 0.1, r * 0.16, -0.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

// ── Spring (blossoms) ─────────────────────────────────────────────────────────
// A spring bouquet — pinks + daffodil yellow, crocus lavender, peach, coral,
// light green and sky blue for variety.
const SPRING_COLORS = ['#ffb7d5', '#ff6f9c', '#ffffff', '#ffe14d', '#c8a2ff', '#b6e3a1', '#a1d9ff', '#ffb07c', '#ff8fbf', '#ffd1e8'];

function drawBlossom(ctx: CanvasRenderingContext2D, s: number, color: string): void {
  ctx.fillStyle = color;
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2;
    ctx.save();
    ctx.translate(Math.cos(a) * s * 0.5, Math.sin(a) * s * 0.5);
    ctx.rotate(a);
    ctx.beginPath();
    ctx.ellipse(0, 0, s * 0.34, s * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  ctx.fillStyle = 'rgba(255,220,120,0.95)';
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.16, 0, Math.PI * 2);
  ctx.fill();
}

function spawnSpring(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 30; i++) {
    particles.push({
      startX: Math.random(),
      startY: -0.1 - Math.random() * 0.6,
      vx: (Math.random() - 0.5) * 0.12,
      vy: 0.28 + Math.random() * 0.22,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 2,
      color: SPRING_COLORS[Math.floor(Math.random() * SPRING_COLORS.length)],
      size: 12 + Math.random() * 12,
    });
  }
  return particles;
}

function drawSpring(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  for (const p of particles) {
    const py = p.startY + p.vy * t;
    if (py > 1.15 || py < -0.15) continue;
    const sway = Math.sin(t * 1.5 + p.rotStart) * 0.05;
    const px = p.startX + p.vx * t + sway;
    const fade = Math.min(1, (py + 0.15) / 0.15) * Math.max(0, Math.min(1, (1.15 - py) / 0.2));
    if (fade <= 0.02) continue;
    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(p.rotStart + p.rotSpeed * t);
    ctx.globalAlpha = fade;
    drawBlossom(ctx, p.size, p.color);
    ctx.restore();
  }
}

// ── Summer (sun) ──────────────────────────────────────────────────────────────
function spawnSummer(): Particle[] {
  // Warm glints float up; the sun itself is drawn from t (no particles needed).
  const particles: Particle[] = [];
  const glint = ['#ffe600', '#ffd000', '#ffb300', '#fff4b0'];
  for (let i = 0; i < 20; i++) {
    particles.push({
      startX: Math.random(),
      startY: 0.5 + Math.random() * 0.55,
      vx: (Math.random() - 0.5) * 0.06,
      vy: -0.12 - Math.random() * 0.14,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: 1 + Math.random() * 2,
      color: glint[Math.floor(Math.random() * glint.length)],
      size: 6 + Math.random() * 10,
    });
  }
  return particles;
}

function drawSummer(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const DUR = 2.8;
  if (t > DUR) return;
  const active = Math.min(1, t / 0.4) * Math.max(0, Math.min(1, (DUR - t) / 0.5));
  const rise = 1 - Math.exp(-t * 2.5);
  const cx = 0.5 * W;
  // Rises from near the bottom (0.82) up to the upper third (0.28).
  const cy = (0.82 - 0.54 * rise) * H;
  const R = 0.13 * Math.min(W, H) * Math.min(1, t / 0.6);

  ctx.globalCompositeOperation = 'lighter';
  // Warm glow.
  const glow = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 3.2);
  glow.addColorStop(0, 'rgba(255,210,90,' + 0.5 * active + ')');
  glow.addColorStop(1, 'rgba(255,180,0,0)');
  ctx.globalAlpha = 1;
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 3.2, 0, Math.PI * 2);
  ctx.fill();
  // Rotating rays.
  ctx.globalAlpha = active * 0.6;
  ctx.strokeStyle = '#ffe07a';
  ctx.lineWidth = Math.max(2, R * 0.06);
  ctx.lineCap = 'round';
  const rays = 12;
  for (let k = 0; k < rays; k++) {
    const a = (k / rays) * Math.PI * 2 + t * 0.5;
    const r1 = R * (1.7 + 0.15 * Math.sin(t * 4 + k));
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * R * 1.25, cy + Math.sin(a) * R * 1.25);
    ctx.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1);
    ctx.stroke();
  }
  // Sun disc.
  ctx.globalAlpha = active;
  const disc = ctx.createRadialGradient(cx, cy, 0, cx, cy, R);
  disc.addColorStop(0, '#fff6c2');
  disc.addColorStop(1, '#ffcf33');
  ctx.fillStyle = disc;
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();
  // Floating warm glints.
  for (const p of particles) {
    const py = p.startY + p.vy * t;
    if (py < 0 || py > 1) continue;
    const px = p.startX + p.vx * t + Math.sin(t * p.rotSpeed + p.rotStart) * 0.02;
    const tw = 0.5 + 0.5 * Math.sin(t * p.rotSpeed * 2 + p.rotStart);
    ctx.globalAlpha = active * tw * 0.8;
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.arc(px * W, py * H, p.size * (0.6 + 0.4 * tw), 0, Math.PI * 2);
    ctx.fill();
  }
}

// ── Rain ──────────────────────────────────────────────────────────────────────
const RAIN_COLORS = ['#a9d6ff', '#cfe8ff', '#e6f3ff'];

function spawnRain(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 160; i++) {
    particles.push({
      startX: Math.random(),
      startY: -0.1 - Math.random() * 1.3, // staggered above the frame
      vx: 0.06 + Math.random() * 0.04, // slight wind
      vy: 1.1 + Math.random() * 0.7,
      rotStart: 0,
      rotSpeed: 0,
      color: RAIN_COLORS[Math.floor(Math.random() * RAIN_COLORS.length)],
      size: 0,
    });
  }
  return particles;
}

function drawRain(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const k = 0.05; // streak length factor
  ctx.lineCap = 'round';
  ctx.lineWidth = 1.6;
  for (const p of particles) {
    const py = p.startY + p.vy * t;
    if (py > 1.12 || py < -0.2) continue;
    const px = p.startX + p.vx * t;
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = p.color;
    ctx.beginPath();
    ctx.moveTo((px - p.vx * k) * W, (py - p.vy * k) * H);
    ctx.lineTo(px * W, py * H);
    ctx.stroke();
  }
}

// ── Snow ──────────────────────────────────────────────────────────────────────
const SNOW_COLORS = ['#ffffff', '#eaf4ff', '#dcecff'];

// A 6-armed snowflake (main arms + little side branches), stroked in the caller's colour.
function drawSnowflake(ctx: CanvasRenderingContext2D, s: number): void {
  ctx.lineCap = 'round';
  ctx.lineWidth = Math.max(1, s * 0.14);
  ctx.beginPath();
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * Math.PI * 2;
    // Main arm.
    ctx.moveTo(0, 0);
    ctx.lineTo(Math.cos(a) * s, Math.sin(a) * s);
    // Two pairs of side branches along the arm.
    for (const f of [0.5, 0.78]) {
      const bx = Math.cos(a) * s * f;
      const by = Math.sin(a) * s * f;
      const bl = s * 0.32 * (1 - f * 0.4);
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(a + Math.PI / 4) * bl, by + Math.sin(a + Math.PI / 4) * bl);
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + Math.cos(a - Math.PI / 4) * bl, by + Math.sin(a - Math.PI / 4) * bl);
    }
  }
  ctx.stroke();
}

function spawnSnow(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 48; i++) {
    particles.push({
      startX: Math.random(),
      startY: -0.1 - Math.random() * 1.0,
      vx: (Math.random() - 0.5) * 0.05,
      vy: 0.16 + Math.random() * 0.18,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: 0.8 + Math.random() * 1.4,
      color: SNOW_COLORS[Math.floor(Math.random() * SNOW_COLORS.length)],
      size: 6 + Math.random() * 8, // arm length — bigger so the flake detail reads
    });
  }
  return particles;
}

function drawSnow(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  for (const p of particles) {
    const py = p.startY + p.vy * t;
    if (py > 1.1 || py < -0.15) continue;
    const sway = Math.sin(t * p.rotSpeed + p.rotStart) * 0.03;
    const px = p.startX + p.vx * t + sway;
    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(p.rotStart + t * 0.5); // slow spin as it falls
    ctx.globalAlpha = 0.9;
    ctx.strokeStyle = p.color;
    drawSnowflake(ctx, p.size);
    ctx.restore();
  }
}

// ── Autumn Leaves ─────────────────────────────────────────────────────────────
const LEAF_COLORS = ['#d2691e', '#ff8c00', '#c0392b', '#e1a94e', '#b5651d', '#e67e22'];

function drawLeaf(ctx: CanvasRenderingContext2D, s: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.5);
  ctx.quadraticCurveTo(s * 0.5, 0, 0, s * 0.5);
  ctx.quadraticCurveTo(-s * 0.5, 0, 0, -s * 0.5);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.25)';
  ctx.lineWidth = Math.max(1, s * 0.04);
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.45);
  ctx.lineTo(0, s * 0.45);
  ctx.stroke();
}

// Maple leaf: stem at the bottom, five lobes fanning upward with concave
// notches between them — an actual leaf silhouette, not a radial star.
function drawMapleLeaf(ctx: CanvasRenderingContext2D, s: number, color: string): void {
  // Right-half outline from the central tip down to the base centre
  // ([x, y] as fractions of s; y negative = up, so the tip is up and stem down).
  const pts = [
    [0.0, -1.0],   // central lobe tip
    [0.16, -0.5],  // notch
    [0.5, -0.62],  // upper-side lobe tip
    [0.34, -0.2],  // notch
    [0.72, -0.05], // lower-side lobe tip (widest)
    [0.28, 0.18],  // notch toward the base
    [0.1, 0.32],   // base
    [0.0, 0.34],   // base centre
  ];
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(pts[0][0] * s, pts[0][1] * s);
  for (let i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0] * s, pts[i][1] * s);
  for (let i = pts.length - 2; i >= 0; i--) ctx.lineTo(-pts[i][0] * s, pts[i][1] * s); // mirror left half
  ctx.closePath();
  ctx.fill();
  // Stem.
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, s * 0.09);
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(0, s * 0.32);
  ctx.lineTo(0, s * 0.72);
  ctx.stroke();
}

// Teardrop/aspen: pointed top, rounded base, with a midrib.
function drawTearLeaf(ctx: CanvasRenderingContext2D, s: number, color: string): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.6);
  ctx.bezierCurveTo(s * 0.5, -s * 0.2, s * 0.45, s * 0.4, 0, s * 0.55);
  ctx.bezierCurveTo(-s * 0.45, s * 0.4, -s * 0.5, -s * 0.2, 0, -s * 0.6);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.22)';
  ctx.lineWidth = Math.max(1, s * 0.04);
  ctx.beginPath();
  ctx.moveTo(0, -s * 0.5);
  ctx.lineTo(0, s * 0.5);
  ctx.stroke();
}

function spawnLeaves(): Particle[] {
  const particles: Particle[] = [];
  for (let i = 0; i < 26; i++) {
    particles.push({
      startX: Math.random(),
      startY: -0.1 - Math.random() * 0.6,
      vx: (Math.random() - 0.5) * 0.16,
      vy: 0.18 + Math.random() * 0.18,
      rotStart: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 3,
      color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)],
      size: 14 + Math.random() * 14,
    });
  }
  return particles;
}

function drawLeaves(ctx: CanvasRenderingContext2D, particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  for (let i = 0; i < particles.length; i++) {
    const p = particles[i];
    const py = p.startY + p.vy * t;
    if (py > 1.15 || py < -0.15) continue;
    const sway = Math.sin(t * 1.8 + p.rotStart) * 0.06;
    const px = p.startX + p.vx * t + sway;
    const fade = Math.max(0, Math.min(1, (1.15 - py) / 0.2));
    ctx.save();
    ctx.translate(px * W, py * H);
    ctx.rotate(p.rotStart + p.rotSpeed * t);
    // Tumble: squash horizontally as it flips over.
    ctx.scale(0.4 + 0.6 * Math.abs(Math.cos(t * 2 + p.rotStart)), 1);
    ctx.globalAlpha = fade;
    // Mix of shapes for variety.
    const kind = i % 3;
    if (kind === 0) drawLeaf(ctx, p.size, p.color);
    else if (kind === 1) drawMapleLeaf(ctx, p.size, p.color);
    else drawTearLeaf(ctx, p.size, p.color);
    ctx.restore();
  }
}

// ── Strobe lights ─────────────────────────────────────────────────────────────
const STROBE_COLORS = ['#ff2ec4', '#00e5ff', '#ffe600', '#22ff5e', '#c04bff', '#ff5a3c'];

function spawnStrobe(): Particle[] {
  return [];
}

function drawStrobe(ctx: CanvasRenderingContext2D, _particles: Particle[], t: number): void {
  const W = ctx.canvas.width;
  const H = ctx.canvas.height;
  const DUR = 1.8;
  if (t > DUR) return;
  const active = Math.max(0, Math.min(1, (DUR - t) / 0.3)); // ease out at the end
  const PERIOD = 0.14; // ~7 flashes/sec
  if ((t % PERIOD) / PERIOD > 0.5) return; // ~50% duty (dark half the time)
  const color = STROBE_COLORS[Math.floor(t / PERIOD) % STROBE_COLORS.length];
  // Partial-opacity ADDITIVE colour wash (video shows through → gentler than a
  // full black/white strobe).
  ctx.globalCompositeOperation = 'lighter';
  ctx.globalAlpha = 0.45 * active;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, W, H);
}

// ── Starter Kit ─────────────────────────────────────────────────────────────
// Ordered by theme: Celebration → Seasons → Weather → Elements & effects.
export const STARTER_VIBES: Vibe[] = [
  // ── Celebration ──
  {
    id: 'confetti',
    name: 'Confetti',
    iconPath: 'M12 2l1.5 4.5L18 8l-4.5 1.5L12 14l-1.5-4.5L6 8l4.5-1.5L12 2zM5 17l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3zm14 0l1 3 3 1-3 1-1 3-1-3-3-1 3-1 1-3z',
    durationMs: 2400,
    spawn: spawnConfetti,
    draw: drawConfetti,
  },
  {
    id: 'fireworks',
    name: 'Fireworks',
    iconPath: 'M12 1l2.2 6.6L21 6l-4.5 5.4L21 18l-6.8-1.6L12 23l-2.2-6.6L3 18l4.5-6.6L3 6l6.8 1.6z',
    // 5 shells popping over ~1.2s, each spark lives ~1.9s → needs the extra room.
    durationMs: 3200,
    spawn: spawnFireworks,
    draw: drawFireworks,
  },
  {
    id: 'balloons',
    name: 'Balloons',
    iconPath: 'M12 2C9 2 7 4.5 7 8c0 3 2 5.5 4.3 6l-.3 1.5c0 .5.5 1 1 1s1-.5 1-1L13 14c2.3-.5 4-3 4-6 0-3.5-2-6-5-6z',
    durationMs: 4200,
    spawn: spawnBalloons,
    draw: drawBalloons,
  },
  {
    id: 'sparkle',
    name: 'Sparkle',
    iconPath: 'M12 3l2 6 6 2-6 2-2 6-2-6-6-2 6-2 2-6z',
    durationMs: 1800,
    spawn: spawnSparkle,
    draw: drawSparkle,
  },
  {
    id: 'hearts',
    name: 'Hearts',
    iconPath: 'M12 21s-7-4.5-9.5-9C1 8.5 3 5 6.5 5 9 5 10.5 6.5 12 8.5 13.5 6.5 15 5 17.5 5 21 5 23 8.5 21.5 12 19 16.5 12 21 12 21z',
    // Longer than the others: hearts are released over ~1.1s and each takes ~2s to
    // rise and fade, so the burst needs room for the last-emitted hearts to finish.
    durationMs: 3200,
    spawn: spawnHearts,
    draw: drawHearts,
  },
  {
    id: 'notes',
    name: 'Notes',
    iconPath: 'M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z',
    durationMs: 3200,
    spawn: spawnNotes,
    draw: drawNotes,
  },
  // ── Seasons ──
  {
    id: 'spring',
    name: 'Spring',
    iconPath: 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6zm0-7a3 3 0 0 1 2.8 4.1A3 3 0 0 1 19 9a3 3 0 0 1-1.1 5.8A3 3 0 0 1 15 19a3 3 0 0 1-3-1.2A3 3 0 0 1 9 19a3 3 0 0 1-2.9-4.2A3 3 0 0 1 5 9a3 3 0 0 1 4.2-2.9A3 3 0 0 1 12 2z',
    durationMs: 4200,
    spawn: spawnSpring,
    draw: drawSpring,
  },
  {
    id: 'summer',
    name: 'Summer',
    iconPath: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM11 1h2v3h-2zM11 20h2v3h-2zM1 11h3v2H1zM20 11h3v2h-3zM4.2 4.2l2.1 2.1-1.4 1.4-2.1-2.1zM17.7 17.7l2.1 2.1-1.4 1.4-2.1-2.1zM19.8 4.2l-2.1 2.1 1.4 1.4 2.1-2.1zM6.3 17.7l-2.1 2.1 1.4 1.4 2.1-2.1z',
    durationMs: 2900,
    spawn: spawnSummer,
    draw: drawSummer,
  },
  {
    id: 'leaves',
    name: 'Autumn Leaves',
    iconPath: 'M17 8C8 10 5.9 16.2 4 21l1.5.5C7 17 10 15 14 14c-3 2-5 4-6 7 6-1 11-5 11-13 0-1-.5-2-2-2z',
    durationMs: 4200,
    spawn: spawnLeaves,
    draw: drawLeaves,
  },
  {
    id: 'snow',
    name: 'Snow',
    iconPath: 'M11 2h2v5.6l4-2.3 1 1.7-5 2.9 5 2.9-1 1.7-4-2.3V22h-2v-5.6l-4 2.3-1-1.7 5-2.9-5-2.9 1-1.7 4 2.3z',
    durationMs: 4500,
    spawn: spawnSnow,
    draw: drawSnow,
  },
  // ── Weather ──
  {
    id: 'rain',
    name: 'Rain',
    iconPath: 'M12 2s6 7 6 11a6 6 0 0 1-12 0c0-4 6-11 6-11z',
    durationMs: 2200,
    spawn: spawnRain,
    draw: drawRain,
  },
  {
    id: 'lightning',
    name: 'Lightning',
    iconPath: 'M13 2L3 14h7v8l10-12h-7z',
    durationMs: 1500,
    spawn: spawnLightning,
    draw: drawLightning,
  },
  // ── Elements & effects ──
  {
    id: 'fire',
    name: 'Fire',
    iconPath: 'M12 2s4 4 4 8a4 4 0 0 1-8 0c0-1 .5-2 1-3-2 1-3 3-3 5a6 6 0 0 0 12 0c0-4-3-7-6-10z',
    durationMs: 2600,
    spawn: spawnFlames,
    draw: drawFlames,
  },
  {
    id: 'bubbles',
    name: 'Bubbles',
    iconPath: 'M7.5 15a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7zm8 5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5zm1-9.5a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
    durationMs: 3600,
    spawn: spawnBubbles,
    draw: drawBubbles,
  },
  {
    id: 'strobe',
    name: 'Strobe Lights',
    iconPath: 'M9 21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1H9v1zM12 2a7 7 0 0 0-4 12.7V18h8v-3.3A7 7 0 0 0 12 2z',
    durationMs: 1900,
    spawn: spawnStrobe,
    draw: drawStrobe,
  },
];

// macOS-reaction-style backdrops: a dim/tint painted behind each burst so the
// effect pops. Keyed by id. Lightning and Strobe are omitted — they already do
// their own full-frame flashing.
const VIBE_BACKDROPS: Record<string, { color: string; peak: number }> = {
  confetti: { color: '#050818', peak: 0.28 },
  fireworks: { color: '#04061a', peak: 0.42 },
  balloons: { color: '#0a1428', peak: 0.16 },
  sparkle: { color: '#050510', peak: 0.25 },
  hearts: { color: '#2a0715', peak: 0.22 },
  notes: { color: '#050518', peak: 0.22 },
  spring: { color: '#231a2a', peak: 0.16 },
  summer: { color: '#2a1e05', peak: 0.18 },
  leaves: { color: '#241203', peak: 0.2 },
  snow: { color: '#0c1626', peak: 0.2 },
  rain: { color: '#0a1220', peak: 0.34 },
  fire: { color: '#1a0500', peak: 0.32 },
  bubbles: { color: '#06202a', peak: 0.16 },
};
for (const v of STARTER_VIBES) {
  if (VIBE_BACKDROPS[v.id]) v.backdrop = VIBE_BACKDROPS[v.id];
}

export type VibeGroup = { name: string; vibes: Vibe[] };

// Grouped view of the same vibes (slices follow the STARTER_VIBES order above).
export const VIBE_GROUPS: VibeGroup[] = [
  { name: 'Celebration', vibes: STARTER_VIBES.slice(0, 6) },
  { name: 'Seasons', vibes: STARTER_VIBES.slice(6, 10) },
  { name: 'Weather', vibes: STARTER_VIBES.slice(10, 12) },
  { name: 'Elements', vibes: STARTER_VIBES.slice(12) },
];
